import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Section from '../ui/Section';
import Button from '../ui/Button';

const Contact = () => {
  const navigate = useNavigate();
  
  // Состояние для хранения данных формы
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  // Состояние для хранения ошибок валидации
  const [errors, setErrors] = useState({});

  // Ошибка отправки на сервер и индикатор загрузки
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
    
    // Очищаем ошибку поля, как только пользователь начинает вводить текст
    if (errors[id]) {
      setErrors(prev => ({ ...prev, [id]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Валидация имени
    if (!formData.name.trim()) {
      newErrors.name = 'Bitte geben Sie Ihren Namen ein.';
    }
    
    // Строгая валидация Email (наличие @ и домена)
    if (!formData.email.trim()) {
      newErrors.email = 'E-Mail ist erforderlich.';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email.trim())) {
      newErrors.email = 'Bitte geben Sie eine gültige E-Mail ein (inkl. @).';
    }
    
    // Валидация телефона (если он введен, проверяем на корректные символы и длину)
    if (formData.phone.trim() && !/^(\+?[0-9\s\-()]){7,20}$/.test(formData.phone.trim())) {
      newErrors.phone = 'Bitte geben Sie eine gültige Nummer ein.';
    }
    
    // Валидация сообщения
    if (!formData.message.trim()) {
      newErrors.message = 'Bitte beschreiben Sie kurz Ihr Vorhaben.';
    }
    
    setErrors(newErrors);
    
    // Возвращаем true, если нет ошибок
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitError('');
    setIsSubmitting(true);
    try {
      const response = await fetch('https://forminit.com/f/msax6w6oyp8', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          blocks: [
            {
              type: 'sender',
              properties: {
                fullName: formData.name,
                email: formData.email,
                phone: formData.phone || undefined,
              },
            },
            { type: 'text', name: 'message', value: formData.message },
          ],
        }),
      });
      const result = await response.json();
      if (result.success) {
        navigate('/danke');
      } else {
        setSubmitError(result.message || 'Beim Senden ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut.');
      }
    } catch (err) {
      setSubmitError('Verbindungsfehler. Bitte versuchen Sie es später erneut.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Вспомогательные функции для стилизации полей с ошибками
  const getWrapperClass = (field) => 
    `flex flex-col border-b transition-colors ${errors[field] ? 'border-red-500/50 focus-within:border-red-500' : 'border-brand-text/20 focus-within:border-brand-text'}`;
    
  const getLabelClass = (field) => 
    `font-mono text-xs uppercase mb-2 ${errors[field] ? 'text-red-500/80' : 'text-brand-text/50'}`;

  return (
    <Section id="contact" className="border-t border-brand-text/10">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        <div>
          <h2 className="text-4xl md:text-5xl lg:text-7xl mb-8 leading-[0.9]">
            GEMEINSAM<br/><span className="text-brand-text/40">SCHAFFEN.</span>
          </h2>
          <p className="font-inter text-brand-text/70 mb-12 max-w-md text-lg">
            Sie träumen vom eigenen, sicheren Zuhause oder möchten als gewerblicher Käufer in wertstabilen Wohnraum investieren? Nutzen Sie unsere Expertise.
          </p>
          
          <div className="space-y-8 font-mono text-sm border-t border-brand-text/10 pt-8 mt-12">
            <div className="grid grid-cols-2 gap-8">
              <div>
                <div className="text-brand-text/50 uppercase mb-2">Office</div>
                <p>51north Real Estate GmbH<br/>Arnsdorf 26<br/>02894 Vierkirchen</p>
              </div>
              <div>
                <div className="text-brand-text/50 uppercase mb-2">Kontakt</div>
                <p>projekte@51north.io</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-brand-text/5 p-8 md:p-12 border border-brand-text/10">
          <h3 className="font-mono text-xs uppercase tracking-widest text-brand-text/50 mb-8">Direktanfrage</h3>
          <form onSubmit={handleSubmit} className="space-y-8" noValidate>
            
            {/* Поле: Имя */}
            <div>
              <div className={getWrapperClass('name')}>
                <label htmlFor="name" className={getLabelClass('name')}>Name / Unternehmen *</label>
                <input 
                  type="text" 
                  id="name" 
                  value={formData.name}
                  onChange={handleChange}
                  className="bg-transparent outline-none pb-3 font-inter text-lg placeholder-brand-text/20" 
                  placeholder="Ihr Name" 
                />
              </div>
              {errors.name && <div className="font-mono text-[10px] text-red-500 mt-2 uppercase tracking-widest">{errors.name}</div>}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Поле: E-Mail */}
              <div>
                <div className={getWrapperClass('email')}>
                  <label htmlFor="email" className={getLabelClass('email')}>E-Mail *</label>
                  <input 
                    type="email" 
                    id="email" 
                    value={formData.email}
                    onChange={handleChange}
                    className="bg-transparent outline-none pb-3 font-inter text-lg placeholder-brand-text/20" 
                    placeholder="mail@firma.de" 
                  />
                </div>
                {errors.email && <div className="font-mono text-[10px] text-red-500 mt-2 uppercase tracking-widest">{errors.email}</div>}
              </div>

              {/* Поле: Телефон */}
              <div>
                <div className={getWrapperClass('phone')}>
                  <label htmlFor="phone" className={getLabelClass('phone')}>Telefonnummer</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    value={formData.phone}
                    onChange={handleChange}
                    className="bg-transparent outline-none pb-3 font-inter text-lg placeholder-brand-text/20" 
                    placeholder="+49..." 
                  />
                </div>
                {errors.phone && <div className="font-mono text-[10px] text-red-500 mt-2 uppercase tracking-widest">{errors.phone}</div>}
              </div>
            </div>

            {/* Поле: Сообщение */}
            <div>
              <div className={getWrapperClass('message')}>
                <label htmlFor="message" className={getLabelClass('message')}>Nachricht / Vorhaben *</label>
                <textarea 
                  id="message" 
                  rows="4" 
                  value={formData.message}
                  onChange={handleChange}
                  className="bg-transparent outline-none pb-3 font-inter text-lg placeholder-brand-text/20 resize-none" 
                  placeholder="Details zu Ihrem Projekt..."
                ></textarea>
              </div>
              {errors.message && <div className="font-mono text-[10px] text-red-500 mt-2 uppercase tracking-widest">{errors.message}</div>}
            </div>
            
            <div>
              <Button type="submit" showArrow className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Wird gesendet…' : 'Anfrage absenden'}
              </Button>
              {submitError && (
                <div className="font-mono text-[10px] text-red-500 mt-3 uppercase tracking-widest">
                  {submitError}
                </div>
              )}
            </div>
          </form>
        </div>
      </div>
    </Section>
  );
};

export default Contact;